import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Clock, Hammer, ShieldAlert } from 'lucide-react';

interface MaintenanceViewProps {
  content?: string;
  excerpt?: string;
}

export const MaintenanceView: React.FC<MaintenanceViewProps> = ({ content, excerpt }) => {
  return (
    <div className="min-h-screen bg-[#020617] text-white flex items-center justify-center p-6 relative overflow-hidden font-sans">
      {/* Background Gradients */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/10 blur-[120px] rounded-full" />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl w-full bg-white/5 border border-white/10 rounded-[2.5rem] p-12 backdrop-blur-xl shadow-2xl relative z-10 text-center"
      >
        <motion.div 
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 15 }}
          className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-lg shadow-indigo-500/20"
        >
          <Hammer size={48} className="text-white" />
        </motion.div>

        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/50">
          System Upgrade in Progress
        </h1>

        <div className="space-y-6">
          {excerpt ? (
            <p className="text-xl text-white/60 font-medium leading-relaxed">
              {excerpt}
            </p>
          ) : (
            <p className="text-xl text-white/60 font-medium leading-relaxed">
              We're currently optimizing the Geospatial Lattice and hardening our neural infrastructure. 
              Access is temporarily restricted to ensure the highest precision for your shopping intelligence.
            </p>
          )}

          {content && (
            <div 
              className="prose prose-invert max-w-none text-white/40 text-sm mt-10 pt-10 border-t border-white/10"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </div>

        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/5 text-[10px] font-black uppercase tracking-widest text-white/40">
            <Clock size={12} className="text-indigo-400" />
            Estimated Restoration: T-Minus 4h
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/5 text-[10px] font-black uppercase tracking-widest text-white/40">
            <ShieldAlert size={12} className="text-emerald-400" />
            Data Integrity: 100% Secured
          </div>
        </div>

        <div className="mt-16 text-[10px] font-black uppercase tracking-[0.3em] text-white/20">
          Precision. Optimization. Intelligence.
        </div>
      </motion.div>

      {/* Decorative Lattice Element */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 opacity-10 flex gap-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
        ))}
      </div>
    </div>
  );
};
